import { useCallback, useEffect } from 'react';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';
import { Photo } from '@/types/photo';
import { useZoomPan } from '@/hooks/useZoomPan';
import { ZoomControls, ZoomLevel } from '../ZoomControls';
import { Spinner } from '@/components/atoms';
import { SurfaceProvider } from '@/theme/SurfaceContext';
import { ThreePane } from '@/components/layout';

export interface PhotoViewerProps {
  photo: Photo;
  className?: string;
}

export function PhotoViewer({ photo, className }: PhotoViewerProps) {
  const {
    zoomLevel,
    isLoading,
    imageSource,
    viewportWarning,
    transformRef,
    containerRef,
    setZoomLevel,
    getCursorStyle,
    handleTransformed,
    handleImageLoadForScale,
  } = useZoomPan({
    photo,
  });

  const handleZoomChange = (level: ZoomLevel) => {
    setZoomLevel(level);
  };

  const handleImageLoad = useCallback((event: React.SyntheticEvent<HTMLImageElement>) => {
    // Calculate initial scale factor for native size zoom calculations
    handleImageLoadForScale(event.currentTarget);
  }, [handleImageLoadForScale]);

  // Custom double-click handler to toggle between fit (scale=1) and 2x zoom (scale=2)
  const handleDoubleClick = useCallback(() => {
    if (!transformRef.current?.instance?.transformState) return;

    const currentScale = transformRef.current.instance.transformState.scale;

    // If at fit (scale=1), zoom to 2x
    // Otherwise, return to fit (scale=1)
    if (Math.abs(currentScale - 1) < 0.1) {
      transformRef.current.centerView(2);
    } else {
      transformRef.current.resetTransform();
    }
  }, [transformRef]);

  // Prevent browser zoom when using trackpad pinch gestures within the PhotoViewer
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleWheel = (event: WheelEvent) => {
      // Trackpad pinch gestures send wheel events with ctrlKey (Windows) or metaKey (Mac)
      if (event.ctrlKey || event.metaKey) {
        event.preventDefault();
      }
    };

    // Attach listener to container element (scoped to PhotoViewer only)
    container.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      container.removeEventListener('wheel', handleWheel);
    };
  }, []);

  const zoom = <ZoomControls currentZoom={zoomLevel} onZoomChange={handleZoomChange} />;

  return (
    <SurfaceProvider level="base" theme="light">
      <ThreePane className={`h-full w-full ${className || ''}`} footer={zoom}>
        <div
          ref={containerRef}
          className="w-full h-full flex items-center justify-center overflow-hidden relative"
          style={{ cursor: getCursorStyle() }}
          onDoubleClick={handleDoubleClick}
        >
          {viewportWarning && (
            <div className="absolute top-16 left-4 right-4 bg-warning text-warning-foreground px-3 py-2 rounded text-sm z-10">
              {viewportWarning}
            </div>
          )}

          {isLoading && (
            <div className="absolute inset-0 flex items-center justify-center bg-background/50 z-20">
              <Spinner size="lg" />
            </div>
          )}

          <TransformWrapper
            ref={transformRef}
            initialScale={1}
            minScale={0.1}
            maxScale={4}
            limitToBounds={true}
            centerOnInit={true}
            doubleClick={{
              disabled: true, // Use custom double-click handler instead
            }}
            wheel={{
              step: 0.1, // Smooth wheel zoom
              smoothStep: 0.005, // Smooth trackpad pinch
            }}
            panning={{
              disabled: false, // Allow panning when zoomed (library handles this automatically)
            }}
            onTransformed={handleTransformed}
          >
            <TransformComponent
              wrapperClass="!w-full !h-full"
              contentClass="!w-full !h-full flex items-center justify-center"
            >
              <img
                src={imageSource}
                alt={photo.title}
                onLoad={handleImageLoad}
                className="w-full h-full object-contain"
                style={{
                  userSelect: 'none',
                }}
                draggable={false}
              />
            </TransformComponent>
          </TransformWrapper>
        </div>
      </ThreePane>
    </SurfaceProvider>
  );
}
